import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessagesSquare, X } from 'lucide-react';
import { useGetAgentById, useUpdateWidget } from 'features/chat/hooks/use-agents';
import { Tabs, TabsContent } from 'components/ui/tabs';
import { AgentIntegrationChatbotScript } from './agent-integration-chatbot-script';
import { AgentIntegrationChatbotDesign } from './agent-integration-chatbot-design';
import { FormProvider, useForm } from 'react-hook-form';
import { parseAllowedDomains, parseChatbotWidgetData } from './utils';
import type { ChatbotWidget } from './types';
import { useToast } from 'hooks/use-toast';
import { WidgetType } from 'features/chat/agent-chat-module/types/agent.service.type';
import { AgentIntegrationGlobalSettings } from './agent-integration-global-settings';
import { zodResolver } from '@hookform/resolvers/zod';
import { useConfig } from 'hooks/use-config';
import { useCommonStore } from 'state/store/common/useCommonStore';
import {
  canConfigureAgentWidget,
  canEditAgentConfig,
} from 'pages/agents/utils/agent-share-access';
import { z } from 'zod';
import { PreviewChatbot } from '../../preview-section/preview-chatbot';
import { parseChatbotWidgetData as parsePreviewWidgetData } from '../../preview-section/preview-chatbot/utils';
import { Skeleton } from 'components/ui/skeleton';
import { AgentDetailsTabBar } from '../../agent-details-tab-bar';
import { AgentIntegrationApp } from './agent-integration-app';
import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';
import { getPickerTheme } from 'pages/agents/utils/agent-config-picker-theme';

type IntegrationCategory = 'browser' | 'app';

const settingsScrollClassName =
  'flex min-h-0 flex-1 flex-col gap-10 overflow-y-auto px-6 py-5 [scrollbar-width:thin] [scrollbar-color:#d1d1d1_transparent] [&::-webkit-scrollbar]:w-[7px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:min-h-[48px] [&::-webkit-scrollbar-thumb]:rounded-[100px] [&::-webkit-scrollbar-thumb]:bg-[#d1d1d1] dark:[scrollbar-color:#d1d1d1_transparent] dark:[&::-webkit-scrollbar-thumb]:bg-[#d1d1d1]';

const validHexaColorReg = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
const colorCheckSchema = z.string().trim().regex(validHexaColorReg).optional();

// The server's own words when it refuses (e.g. no usage credential configured), so the
// user can act on it or report it to an administrator.
const serverMessage = (error: unknown): string | undefined => {
  const data = (error as { response?: { data?: { detail?: unknown; error?: unknown } } })
    ?.response?.data;
  const value = data?.detail ?? data?.error;
  return typeof value === 'string' && value.trim() ? value : undefined;
};

const ChatbotWidgetSchema = z.object({
  name: z.string().trim(),
  logo_url: z.string().trim(),
  logo_id: z.string().trim().nullable(),
  greeting: z.string().trim().nonempty(),
  site_url: z.string().trim().nullable(),
  brand_color: colorCheckSchema.nullable(),
  allowed_domains: z.string().optional(),
  allow_visitor_tool_connect: z.boolean(),
  host_cookie_name: z.string(),
  designSettings: z.object({
    headerStartColor: colorCheckSchema,
    headerEndColor: colorCheckSchema,
    titleColor: colorCheckSchema,
    chatBackgroundColor: colorCheckSchema,
    chatFontColor: colorCheckSchema,
    sendButtonBackgroundColor: colorCheckSchema,
    sendButtonFontColor: colorCheckSchema,
    chatButtonBgColor: colorCheckSchema,
    borderColor: colorCheckSchema,
    'chat-toggle-button--background-color': colorCheckSchema,
    'chat-toggle-button--color': colorCheckSchema,
    banner: z.object({
      enabled: z.boolean(),
      imageUrl: z.string(),
      imageId: z.string().optional(),
      text: z.string(),
    }),
  }),
});

type AgentIntegrationsChatbotProps = {
  agentId: string;
  allowedDomainsInput: string;
  onAllowedDomainsInputChange: (value: string) => void;
};

export const AgentIntegrationsChatbot = ({
  agentId,
  allowedDomainsInput,
  onAllowedDomainsInputChange,
}: AgentIntegrationsChatbotProps) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const ui = getPickerTheme(theme === 'dark');
  const [category, setCategory] = useState<IntegrationCategory>('browser');
  const [panel, setPanel] = useState('design');
  const isBrowserIntegration = category === 'browser';
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const { toast } = useToast();
  const userId = useCommonStore((state) => state.user?.id);

  const { data, isLoading } = useGetAgentById(
    { id: agentId, projectKey: blocksKey },
    { enabled: !!blocksKey }
  );
  const agent = data?.agent;
  // Server-decided (can_configure_widget): creator in the agent's own org, org admin or
  // an explicit editor for one shared in from another.
  const canEdit = canConfigureAgentWidget(agent, userId);
  // The page header offers Save & Publish only to an agent editor. Someone who may
  // configure the widget but not the agent gets no header action, so this tab carries
  // its own Save for them.
  const showOwnSave = canEdit && !canEditAgentConfig(agent, userId);

  const form = useForm<ChatbotWidget>({
    values: {
      ...parseChatbotWidgetData(agent?.widget ?? undefined),
      allowed_domains: allowedDomainsInput,
    },
    resolver: zodResolver(ChatbotWidgetSchema),
    mode: 'all',
  });

  const { mutateAsync } = useUpdateWidget();
  const [isRegeneratingWidgetId, setIsRegeneratingWidgetId] = useState(false);
  const [chatbotKey, setChatbotKey] = useState(0);
  const [isPreviewChatOpen, setIsPreviewChatOpen] = useState(true);
  const widget = form.watch();

  const resetChat = () => {
    setChatbotKey((current) => current + 1);
  };

  const previewWidget = useMemo(() => {
    const base = parsePreviewWidgetData(agent?.widget ?? undefined);
    if (!agent) {
      return base;
    }
    return {
      ...base,
      name: widget.name || base.name,
      greeting: widget.greeting || base.greeting,
      logo_url: widget.logo_url || base.logo_url,
      logo_id: widget.logo_id ?? base.logo_id,
      site_url: widget.site_url ?? base.site_url,
      brand_color: widget.brand_color ?? base.brand_color,
      designSettings: {
        ...base.designSettings,
        ...widget.designSettings,
      },
    };
  }, [agent, widget]);

  const buildWidgetPayload = (values: ChatbotWidget) => {
    if (!agent) return null;

    // agent.widget is null until this org has configured its own widget: the config is
    // per-org now, so a shared agent starts blank here rather than showing the owner's.
    const restSettings = (agent.widget?.widget_settings ?? []).filter(
      (item) => item.widget_type !== WidgetType.CHAT
    );

    return {
      ...(agent.widget ?? {}),
      name: values.name,
      logo_url: values.logo_url,
      greeting: values.greeting,
      site_url: values.site_url || null,
      brand_color: values.brand_color || null,
      allowed_domains: parseAllowedDomains(values.allowed_domains),
      allow_visitor_tool_connect: values.allow_visitor_tool_connect,
      host_cookie_name: values.host_cookie_name.trim() || null,
      widget_settings: [
        ...restSettings,
        {
          widget_type: WidgetType.CHAT,
          fe_style: JSON.stringify(values.designSettings),
        },
      ],
    };
  };

  const submitHandler = async (values: ChatbotWidget) => {
    if (!agent || !canEdit) return;
    try {
      const widgetPayload = buildWidgetPayload(values);
      if (!widgetPayload) return;

      const res = await mutateAsync({
        agent_id: agentId,
        project_key: blocksKey,
        regen_widget_id: false,
        widget: widgetPayload,
      });
      if (!res.is_success) {
        toast({
          title: t('ERROR'),
          description: t('FAILED_TO_UPDATE_CHATBOT'),
          variant: 'destructive',
        });
        return;
      }
      // Saved, but billing is not configured, so the widget cannot answer anyone yet.
      const unbillable = res.data?.usage_credential_problem;
      toast({
        variant: unbillable ? 'destructive' : 'success',
        title: unbillable ? t('WARNING') || 'Warning' : t('SUCCESS'),
        description: unbillable ?? t('CHATBOT_UPDATED_SUCCESSFULLY'),
      });
    } catch (error) {
      toast({
        title: t('ERROR'),
        description: serverMessage(error) ?? t('SOMETHING_WENT_WRONG'),
        variant: 'destructive',
      });
    }
  };

  const handleRegenerateWidgetId = async () => {
    if (!agent || !canEdit) return;

    try {
      setIsRegeneratingWidgetId(true);

      const res = await mutateAsync({
        agent_id: agentId,
        project_key: blocksKey,
        regen_widget_id: true,
      });

      if (!res.is_success) {
        toast({
          title: t('ERROR'),
          description:
            t('FAILED_TO_REGENERATE_WIDGET_ID') || 'Failed to regenerate widget ID.',
          variant: 'destructive',
        });
        return;
      }

      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description:
          t('WIDGET_ID_REGENERATED_SUCCESSFULLY') ||
          'Widget ID regenerated successfully.',
      });
    } catch (error) {
      toast({
        title: t('ERROR'),
        description: serverMessage(error) ?? t('SOMETHING_WENT_WRONG'),
        variant: 'destructive',
      });
    } finally {
      setIsRegeneratingWidgetId(false);
    }
  };

  return (
    <div
      data-testid="agent-integrations-workspace"
      className="flex h-full min-h-0 w-full flex-col gap-4 overflow-hidden lg:flex-row lg:gap-5"
    >
      <div
        className={cn(
          'flex min-h-0 min-w-0 flex-col overflow-hidden',
          isBrowserIntegration ? 'flex-1' : 'w-full',
        )}
      >
        <div
          data-testid="agent-integrations-card"
          className={cn(
            'flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-3xl border shadow-sm',
            ui.dialog,
          )}
        >
        {isLoading ? (
          <div className="flex flex-col gap-6 px-6 py-5">
            <Skeleton className="h-8 w-40" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : (
          <>
            <div className={cn('shrink-0 px-6 pb-4 pt-6', ui.header)}>
              <div className="flex w-full shrink-0 flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
                <h2 className={cn('shrink-0 text-[1.65rem] font-bold leading-tight tracking-tight', ui.title)}>
                  {t('CHATBOT_INTGERATION', { defaultValue: 'Integration' })}
                </h2>

                <AgentDetailsTabBar
                  variant="inline"
                  className="w-auto max-w-full shrink-0 sm:ml-auto"
                  tabsClassName="w-auto"
                  tabs={[
                    {
                      href: 'browser',
                      label: t('BROWSER_INTEGRATION', {
                        defaultValue: 'Browser Integration',
                      }),
                    },
                    {
                      href: 'app',
                      label: t('APP_INTEGRATION', { defaultValue: 'App Integration' }),
                    },
                  ]}
                  activeHref={category}
                  onTabClick={(href) => setCategory(href as IntegrationCategory)}
                />
              </div>
            </div>

            <div className={settingsScrollClassName}>
            {isBrowserIntegration ? (
              <FormProvider {...form}>
                <form
                  onSubmit={form.handleSubmit(submitHandler)}
                  className="flex min-h-0 flex-1 flex-col gap-10"
                >
                  <Tabs
                    value={panel}
                    onValueChange={setPanel}
                    className="flex w-full flex-col gap-10"
                  >
                    <AgentDetailsTabBar
                      variant="fullWidth"
                      className="w-full"
                      tabs={[
                        { href: 'design', label: t('DESIGN') || 'Design' },
                        { href: 'publish', label: t('PUBLISH') || 'Publish' },
                        // { href: 'global', label: t('GLOBAL') || 'Global' },
                      ]}
                      activeHref={panel}
                      onTabClick={setPanel}
                    />

                    <fieldset
                      disabled={!canEdit}
                      className="m-0 min-w-0 border-0 p-0 disabled:opacity-70"
                    >
                      <TabsContent
                        value="design"
                        className="mt-0 data-[state=inactive]:hidden"
                      >
                        <AgentIntegrationChatbotDesign canEdit={canEdit} />
                      </TabsContent>
                      <TabsContent
                        value="publish"
                        className="mt-0 data-[state=inactive]:hidden"
                      >
                        <AgentIntegrationChatbotScript
                          widgetId={agent?.widget_id ?? ''}
                          projectKey={blocksKey}
                          appDomainName={configurationSettings?.paths.baseUrl || ''}
                          onAllowedDomainsInputChange={onAllowedDomainsInputChange}
                          onRegenerateWidgetId={handleRegenerateWidgetId}
                          isRegeneratingWidgetId={isRegeneratingWidgetId}
                          canEdit={canEdit}
                          showSave={showOwnSave}
                        />
                      </TabsContent>
                      <TabsContent
                        value="global"
                        className="mt-0 data-[state=inactive]:hidden"
                      >
                        <AgentIntegrationGlobalSettings />
                      </TabsContent>
                    </fieldset>
                  </Tabs>
                </form>
              </FormProvider>
            ) : (
              <AgentIntegrationApp agentId={agentId} canEdit={canEdit} />
            )}
            </div>
          </>
        )}
        </div>
      </div>

      {isBrowserIntegration ? (
        <aside className="flex min-h-0 w-full flex-1 flex-col overflow-hidden lg:h-auto lg:w-[340px] lg:flex-none xl:w-[360px]">
          <div
            data-testid="agent-integrations-preview"
            className={cn(
              'flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-3xl border shadow-sm',
              ui.dialog,
            )}
          >
            <div className={cn('shrink-0 px-5 pb-3 pt-5', ui.header)}>
              <h2 className={cn('text-lg font-bold leading-tight tracking-tight', ui.title)}>
                {t('PREVIEW', { defaultValue: 'Preview' })}
              </h2>
            </div>
            <div className="flex min-h-0 flex-1 flex-col px-4 pb-4 pt-3">
              <div className="relative min-h-0 w-full flex-1">
                {isLoading ? (
                  <Skeleton className="h-full w-full rounded-2xl" />
                ) : agent ? (
                  <>
                    <div
                      className={cn(
                        'absolute inset-0 bottom-12 overflow-hidden rounded-2xl',
                        !isPreviewChatOpen && 'hidden',
                      )}
                    >
                      <PreviewChatbot
                        key={chatbotKey}
                        agentId={agent.id}
                        widgetId={agent.widget_id}
                        widget={previewWidget}
                        projectKey={blocksKey}
                        lastUpdatedDate={agent.last_updated_date}
                        onResetChat={resetChat}
                        variant="figma-edit"
                        applyWidgetColors
                        className="h-full w-full"
                      />
                    </div>
                    {!isPreviewChatOpen ? (
                      <div
                        data-testid="preview-chat-closed"
                        className="absolute inset-0 bottom-12 flex flex-col items-center justify-center gap-3 px-6 text-center"
                      >
                        <div
                          className={cn(
                            'flex size-12 items-center justify-center rounded-full',
                            ui.emptyIcon,
                          )}
                        >
                          <MessagesSquare className="size-5" />
                        </div>
                        <p className={cn('text-sm', ui.muted)}>
                          {t('OPEN_CHAT_TO_PREVIEW', {
                            defaultValue: 'Open the chat to preview',
                          })}
                        </p>
                      </div>
                    ) : null}
                    <button
                      type="button"
                      data-testid="preview-chat-toggle"
                      className="absolute bottom-0 right-0 flex size-10 items-center justify-center rounded-full text-white shadow-md"
                      style={{
                        backgroundColor:
                          previewWidget.designSettings['chat-toggle-button--background-color'] ||
                          '#000000',
                        color:
                          previewWidget.designSettings['chat-toggle-button--color'] || '#FFFFFF',
                      }}
                      aria-pressed={isPreviewChatOpen}
                      aria-label={
                        isPreviewChatOpen
                          ? t('CLOSE_CHAT') || 'Close chat'
                          : t('OPEN_CHAT') || 'Open chat'
                      }
                      onClick={() => setIsPreviewChatOpen((open) => !open)}
                    >
                      {isPreviewChatOpen ? (
                        <X className="size-4" />
                      ) : (
                        <MessagesSquare className="size-4" />
                      )}
                    </button>
                  </>
                ) : (
                  <p className={cn('flex h-full items-center justify-center px-6 text-sm', ui.muted)}>
                    {t('NO_PREVIEW_AVAILABLE', { defaultValue: 'No preview available' })}
                  </p>
                )}
              </div>
            </div>
          </div>
        </aside>
      ) : null}
    </div>
  );
};
